import type {GroupHeaderFn} from './types'
import {getToolIcon} from '../tool/ToolIcon'
import {TOOL_GROUP_DISPLAY_CONFIG} from '../../../constants/tool-names'

/**
 * Helper to create a group header with a title and icon
 */
function createHeader(title: string, iconToolName: string, mcpToolNameMappings?: Record<string, string>) {
  return {
    title,
    leadingVisual: getToolIcon(iconToolName, mcpToolNameMappings),
  }
}

/**
 * Type guard to check if a tool name has display config
 */
function hasDisplayConfig(toolName: string): toolName is keyof typeof TOOL_GROUP_DISPLAY_CONFIG {
  return toolName in TOOL_GROUP_DISPLAY_CONFIG
}

/**
 * Creates a header function that creates a title based on tool type and count.
 * Accepts optional MCP tool name mappings to show the MCP icon for MCP tools.
 */
export function createGroupHeader(mcpToolNameMappings?: Record<string, string>): GroupHeaderFn {
  return group => {
    const toolName = group.metadata?.toolName as string | undefined
    const count = group.count

    if (!toolName) {
      return {
        title: `${count} operations`,
        leadingVisual: undefined,
      }
    }

    // Use the display config if available
    if (hasDisplayConfig(toolName)) {
      const displayConfig = TOOL_GROUP_DISPLAY_CONFIG[toolName]
      if (displayConfig) {
        return createHeader(displayConfig.getTitle(count), displayConfig.iconToolName, mcpToolNameMappings)
      }
    }

    // Fallback for unknown tools
    return {
      title: `${toolName}`,
      leadingVisual: getToolIcon(toolName, mcpToolNameMappings),
    }
  }
}

/**
 * Header function that creates a title based on tool type and count.
 */
export const defaultGroupHeader: GroupHeaderFn = createGroupHeader()
